import { CanAccess } from '@modules/common/components/CanAccess';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import {
  DataIngestionInProgressIndicator,
  DisabledIntegrationIndicator,
} from '@modules/integrationsV2/components/IntegrationStateIndicator';
import { MetadataFieldMappingActionsMenu } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingActionsMenu';
import { MetadataFieldMappingAutoComplete } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingAutoComplete';
import { MetadataFieldMappingLinkIcon } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingLinkIcon';
import { MetadataFieldMappingStateIndicator } from '@modules/integrationsV2/components/LinkIntegrationMetadataSection/MetadataFieldMappingList/MetadataFieldMappingStateIndicator';
import { useUpdateMetadataFieldRelations } from '@modules/integrationsV2/hooks';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getExistingFieldsRelation,
  trackIntegrationPageAddLinkEvent,
} from '@modules/integrationsV2/utils';
import { getEqlFieldTypeFromStringField } from '@modules/integrationsV2/utils/getMetadataFieldRelationsByIntegrationType';
import { Box, Stack, Typography } from '@mui/material';
import {
  EqlField,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useEffect, useState } from 'react';

interface MetadataFieldMappingListItemProps {
  sourceIntegrationSource: IntegrationSource;
  targetIntegrationSource: IntegrationSource;
  eqlFields: EqlField[];
}

export const MetadataFieldMappingListItem = ({
  sourceIntegrationSource,
  targetIntegrationSource,
  eqlFields,
}: MetadataFieldMappingListItemProps) => {
  const canLinkMetadataFields = useIsAuthorized({
    operation: PermissionOperation.Write,
    resource: PermissionResource.Metadata,
  });

  const fieldRelationsByType = useIntegrationsStore(
    ({ data }) => data.fieldRelationsByType,
  );
  const isFieldRelationsQueryInProgress = useIntegrationsStore(
    ({ view }) => view.isFieldRelationsQueryInProgress,
  );

  const { relatedSourceField, relatedTargetField } = getExistingFieldsRelation({
    fieldRelationsByType,
    sourceIntegrationSource,
    targetIntegrationSource,
  });

  const [sourceField, setSourceField] = useState(relatedSourceField ?? '');
  const [targetField, setTargetField] = useState(relatedTargetField ?? '');

  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setSourceField(relatedSourceField ?? '');
  }, [relatedSourceField]);

  useEffect(() => {
    setTargetField(relatedTargetField ?? '');
  }, [relatedTargetField]);

  const [updateFieldRelations, { isLoading: isUpdatingFieldRelations }] =
    useUpdateMetadataFieldRelations();

  const sourceEqlField = getEqlFieldTypeFromStringField(
    sourceField,
    eqlFields,
    sourceIntegrationSource,
  );

  const targetEqlField = getEqlFieldTypeFromStringField(
    targetField,
    eqlFields,
    targetIntegrationSource,
  );

  const sourceIntegrationFieldDetails = sourceEqlField
    ? {
        displayName: sourceEqlField.displayName,
        objectType: sourceEqlField.objectType,
      }
    : undefined;

  const targetIntegrationFieldDetails = targetEqlField
    ? {
        displayName: targetEqlField.displayName,
        objectType: targetEqlField.objectType,
      }
    : undefined;

  const onTargetFieldChange = async (
    _: React.SyntheticEvent<Element, Event>,
    eqlField: EqlField | null,
  ) => {
    if (!eqlField) {
      return;
    }

    setErrorMessage('');
    setTargetField(eqlField.field);

    if (eqlField.field === sourceField) {
      setErrorMessage('Duplicate field');
      return;
    }

    if (!sourceField || !eqlField.field) {
      return;
    }

    try {
      await updateFieldRelations({
        sourceFieldInput: {
          integrationSource: sourceIntegrationSource,
          field: sourceField,
        },
        targetFieldInput: {
          integrationSource: targetIntegrationSource,
          field: eqlField.field,
        },
      });
      if (targetEqlField && sourceEqlField) {
        trackIntegrationPageAddLinkEvent({
          eventName: 'Link Integrations - Metadata Link Added',
          targetIntegrationField: targetIntegrationFieldDetails,
          sourceIntegrationField: sourceIntegrationFieldDetails,
        });
      }
    } catch {
      // do nothing
    }
  };

  const onSourceFieldChange = async (
    _: React.SyntheticEvent<Element, Event>,
    eqlField: EqlField | null,
  ) => {
    if (!eqlField) {
      return;
    }

    setErrorMessage('');
    setSourceField(eqlField.field);

    if (eqlField.field === targetField) {
      setErrorMessage('Duplicate field');
      return;
    }

    if (!targetField || !eqlField.field) {
      return;
    }

    try {
      await updateFieldRelations({
        sourceFieldInput: {
          integrationSource: sourceIntegrationSource,
          field: eqlField.field,
        },
        targetFieldInput: {
          integrationSource: targetIntegrationSource,
          field: targetField,
        },
      });
      trackIntegrationPageAddLinkEvent({
        eventName: 'Link Integrations - Metadata Link Added',
        targetIntegrationField: targetIntegrationFieldDetails,
        sourceIntegrationField: sourceIntegrationFieldDetails,
      });
    } catch {
      // do nothing
    }
  };

  const isLinked = !!relatedSourceField && !!relatedTargetField;

  const onRemoveLink = async () => {
    setErrorMessage('');

    if (!isLinked || !sourceField || !targetField) {
      return;
    }

    try {
      await updateFieldRelations({
        sourceFieldInput: {
          integrationSource: sourceIntegrationSource,
          field: sourceField,
        },
        targetFieldInput: {
          integrationSource: targetIntegrationSource,
          field: targetField,
        },
        isRemoveAction: true,
      });
      trackIntegrationPageAddLinkEvent({
        eventName: 'Link Integrations - Metadata Link Removed',
        targetIntegrationField: targetIntegrationFieldDetails,
        sourceIntegrationField: sourceIntegrationFieldDetails,
      });
    } catch {
      // do nothing
    }
  };

  const isIngestionInProgress = targetIntegrationSource.isIngestionInProgress;

  const isIntegrationDisabled = targetIntegrationSource.isDisabled;

  const isLinkingDisabled =
    isIngestionInProgress ||
    isUpdatingFieldRelations ||
    isFieldRelationsQueryInProgress ||
    !canLinkMetadataFields;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.5}
    >
      <Stack minWidth="100px" spacing={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          columnGap={1}
          rowGap={0.5}
          flexWrap="wrap"
        >
          <Typography>{targetIntegrationSource.channel.displayName}</Typography>
          {isIntegrationDisabled && (
            <DisabledIntegrationIndicator variant="icon" />
          )}
          {isIngestionInProgress && (
            <DataIngestionInProgressIndicator variant="icon" />
          )}
          <MetadataFieldMappingStateIndicator isLinked={isLinked} />
        </Stack>
        {targetIntegrationSource.namespace && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {targetIntegrationSource.namespace}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <CanAccess
          operation={PermissionOperation.Write}
          resource={PermissionResource.Metadata}
        >
          <MetadataFieldMappingAutoComplete
            integrationSource={targetIntegrationSource}
            onChange={onTargetFieldChange}
            value={targetEqlField}
            isDisabled={isLinkingDisabled}
            isLoading={isFieldRelationsQueryInProgress}
            isLinked={isLinked}
            eqlFields={eqlFields}
            isError={!!errorMessage}
          />
        </CanAccess>
        <Stack width="80px" alignItems="center" justifyContent="center">
          <MetadataFieldMappingLinkIcon
            sourceEqlField={sourceEqlField}
            targetEqlField={targetEqlField}
            isLoading={
              isUpdatingFieldRelations || isFieldRelationsQueryInProgress
            }
            isLinked={isLinked}
            errorMessage={errorMessage}
          />
        </Stack>
        <CanAccess
          operation={PermissionOperation.Write}
          resource={PermissionResource.Metadata}
        >
          <MetadataFieldMappingAutoComplete
            integrationSource={sourceIntegrationSource}
            onChange={onSourceFieldChange}
            value={sourceEqlField}
            isDisabled={isLinkingDisabled}
            isLoading={isFieldRelationsQueryInProgress}
            isLinked={isLinked}
            eqlFields={eqlFields}
            isError={!!errorMessage}
          />
        </CanAccess>
        <Box width="24px">
          {isLinked && !isLinkingDisabled && (
            <MetadataFieldMappingActionsMenu onRemoveLink={onRemoveLink} />
          )}
        </Box>
      </Stack>
    </Stack>
  );
};
